import { Link } from 'react-router-dom';
import { Typography, Box, Card, CardContent, Button, Container, Paper, Tooltip, Grid } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Footer from '../components/Footer';

function Home() {
  return (
    <Box
      sx={{
        scrollSnapType: 'both mandatory',
        scrollBehavior: 'smooth',
        scrollPaddingTop: '0',
        overflowY: 'scroll',
        transitionDuration: '10s',
      }}
    >
      <Box
        sx={{
          scrollSnapAlign: 'start',
          position: 'relative',
          minHeight: { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' },
          background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          px: { xs: 2, sm: 3 },
          overflow: 'hidden',
          '::before': {
            content: '""',
            position: 'absolute',
            bottom: -100,
            right: -100,
            width: 400,
            height: 400,
            background: 'radial-gradient(circle, rgba(33,150,243,0.50) 0%, transparent 60%)',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth='xl' sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant='overline'
            color='text.secondary'
            fontWeight={500}
            fontSize={{ xs: '0.7rem', sm: '0.75rem' }}
            letterSpacing={1}
            textTransform='uppercase'
            mb={1}
            sx={{ pb: { xs: 4, sm: 6 } }}
          >
            The B2B Referral Platform
          </Typography>

          <Typography
            fontWeight='bold'
            color='primary'
            gutterBottom
            sx={{
              fontSize: { xs: '32px', sm: '40px', md: '50px' },
              lineHeight: 1.35,
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0,0,0,0.05)',
            }}
          >
            Get warm intros that land.
          </Typography>

          <Typography
            fontWeight='bold'
            color='primary'
            gutterBottom
            sx={{
              fontSize: { xs: '32px', sm: '40px', md: '50px' },
              lineHeight: 1.35,
              mt: -1,
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0,0,0,0.05)',
            }}
          >
            Or earn by making them.
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '15px', sm: '16px', md: '17px' },
              fontWeight: 400,
              color: 'text.secondary',
              maxWidth: '600px',
              mt: { xs: 1, sm: 2 },
              mb: { xs: 4, sm: 6, md: 8 },
              mx: 'auto',
              textAlign: 'center',
            }}
          >
            Built for scale — no admin, no friction, no delays. <br />
            For tech companies and industry professionals.
          </Typography>

          <Button
            variant='contained'
            color='primary'
            size='large'
            sx={{
              px: { xs: 3, sm: 4 },
              py: { xs: 1, sm: 1.5 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 'bold',
              borderRadius: '999px',
              boxShadow: '0px 4px 20px rgba(0,0,0,0.15)',
              textTransform: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              '&:hover .arrow-icon': {
                transform: 'translateX(4px)',
              },
            }}
            onClick={() => {
              document.getElementById('referral-platform')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Get Started
            <ArrowForwardIcon className='arrow-icon' sx={{ transition: 'transform 0.2s ease' }} />
          </Button>

          <Box mt={2} display='flex' justifyContent='center' alignItems='center'>
            <Typography variant='caption' color='text.secondary' sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              No pitching ✦ No overhead
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Page 1 - Adjusted */}
      <Box
        id='referral-platform'
        sx={{
          scrollSnapAlign: 'start',
          minHeight: { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' },
          background: 'linear-gradient(135deg, #eef2f6 0%, #ffffff 100%)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pt: { xs: 2, sm: 0 },
          mt: 0,
        }}
      >
        <Container maxWidth='xl'>
          <Box>
            <Paper
              elevation={3}
              sx={{
                borderRadius: 4,
                pt: { xs: 2, sm: 3, md: 4 },
                pb: { xs: 2, sm: 3, md: 4 },
                px: { xs: 2, sm: 3, md: 4 },
                background: 'radial-gradient(circle at top left, #f4f7ff, #ffffff)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                position: 'relative',
                overflow: 'hidden',
                maxWidth: 1220,
                margin: '0 auto',
              }}
            >
              <Box mb={{ xs: 4, sm: 6 }} sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                    }}
                  >
                    <Typography
                      variant='h2'
                      fontWeight='bold'
                      color='primary'
                      sx={{ fontSize: { xs: '34px', sm: '42px', md: '50px' }, textAlign: 'right' }}
                    >
                      The B2B Referral Platform
                    </Typography>
                    <Typography
                      variant='h6'
                      fontWeight={300}
                      color='text.secondary'
                      sx={{
                        minWidth: 'fit-content',
                        fontSize: { xs: '14px', sm: '16px', md: '18px' },
                        textAlign: 'right',
                        clear: 'both',
                      }}
                    >
                      for Emerging Tech Companies and Senior Industry Professionals
                    </Typography>
                  </Box>
                </Box>
                <Box mt={{ xs: 3, sm: 6 }}>
                  <Typography variant='body1' color='text.secondary' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                    One side brings the product.
                  </Typography>
                  <Typography variant='body1' color='text.secondary' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                    The other brings the connections.
                  </Typography>
                  <Typography variant='body1' color='text.secondary' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                    We handle comms, tracking, contracts, admin and payments.
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={{ xs: 2, sm: 3 }} alignItems='stretch'>
                <Grid item xs={12} md={6}>
                  <Card
                    variant='outlined'
                    sx={{
                      borderRadius: 3,
                      width: '100%',
                      height: '100%',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                      transition: 'all 0.3s',
                      '&:hover': { transform: 'translateY(-5px)' },
                    }}
                  >
                    <CardContent
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box display='flex' alignItems='center' mb={1}>
                        <PeopleIcon fontSize='large' color='primary' />
                        <Typography variant='h6' fontWeight='bold' ml={1} sx={{ fontSize: { xs: '16px', sm: '18px' } }}>
                          Industry Professionals
                        </Typography>
                      </Box>
                      <Typography variant='body2' mb={2} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                        You know decision-makers. Earn passive income by introducing them to tech companies that need
                        warm intros.
                      </Typography>
                      <Box
                        mt={2}
                        flexGrow={1}
                        display='flex'
                        flexDirection='column'
                        alignItems='start'
                        justifyContent='end'
                      >
                        <Tooltip
                          title="Sign up and get access within a day to view active programs and check if it's a match."
                          arrow
                        >
                          <Button
                            variant='contained'
                            color='primary'
                            component={Link}
                            to='/register'
                            sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                          >
                            Become a Lead Agent
                          </Button>
                        </Tooltip>
                        <Box display='flex' alignItems='center' gap={1} mt={0.5}>
                          <ArrowForwardIcon
                            fontSize='small'
                            color='primary'
                            sx={{ transition: 'transform 0.2s ease', '&:hover': { transform: 'translateX(3px)' } }}
                          />
                          <Link
                            to='#lead-agents'
                            onClick={(e) => {
                              e.preventDefault();
                              document.getElementById('lead-agents')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            style={{
                              fontWeight: 400,
                              fontSize: '0.85rem',
                              textDecoration: 'none',
                              color: '#1976d2',
                              transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                            onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                          >
                            Here's how it works
                          </Link>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card
                    variant='outlined'
                    sx={{
                      borderRadius: 3,
                      width: '100%',
                      height: '100%',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                      transition: 'all 0.3s',
                      '&:hover': { transform: 'translateY(-5px)' },
                    }}
                  >
                    <CardContent
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box display='flex' alignItems='center' mb={1}>
                        <IntegrationInstructionsIcon fontSize='large' color='primary' />
                        <Typography variant='h6' fontWeight='bold' ml={1} sx={{ fontSize: { xs: '16px', sm: '18px' } }}>
                          Tech Companies
                        </Typography>
                      </Box>
                      <Typography variant='body2' mb={2} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                        Getting in front of decision-makers is harder than ever. Grow with warm intros from insiders who
                        already know your buyers.
                      </Typography>
                      <Box
                        mt={2}
                        flexGrow={1}
                        display='flex'
                        flexDirection='column'
                        alignItems='start'
                        justifyContent='end'
                      >
                        <Box display='flex' justifyContent='flex-end'>
                          <Box>
                            <Tooltip
                              title='Sign up to share your product and commission plan. We may connect you with industry insiders who know your buyers, and you can also invite your own.'
                              arrow
                            >
                              <Button
                                variant='contained'
                                color='primary'
                                component={Link}
                                to='/register'
                                sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                              >
                                Register as a Client
                              </Button>
                            </Tooltip>
                            <Box display='flex' alignItems='center' gap={1} mt={0.5}>
                              <ArrowForwardIcon
                                fontSize='small'
                                color='primary'
                                sx={{ transition: 'transform 0.2s ease', '&:hover': { transform: 'translateX(3px)' } }}
                              />
                              <Link
                                to='#clients'
                                onClick={(e) => {
                                  e.preventDefault();
                                  document.getElementById('clients')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                style={{
                                  fontWeight: 400,
                                  fontSize: '0.85rem',
                                  textDecoration: 'none',
                                  color: '#1976d2',
                                  transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.textDecoration = 'underline')}
                                onMouseLeave={(e) => (e.target.style.textDecoration = 'none')}
                              >
                                Learn more
                              </Link>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Container>
      </Box>

      <Box
        id='lead-agents'
        sx={{
          scrollSnapAlign: 'start',
          minHeight: { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' },
          backgroundColor: '#f4f7ff',
          py: { xs: 4, sm: 6 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ py: 0, maxWidth: 1220 }}>
          <Box
            textAlign='center'
            mb={{
              xs: 3,
              sm: 4,
            }}
          >
            <Typography
              variant='h4'
              fontWeight='bold'
              color='primary'
              gutterBottom
              sx={{ fontSize: { xs: '24px', sm: '32px', md: '34px' } }}
            >
              Monetize your network — on your terms
            </Typography>
            <Typography variant='subtitle1' color='text.secondary' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
              Why industry professionals do B2B referrals
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 2, sm: 3 }} mb={{ xs: 3, sm: 4 }}>
            {[
              'Earn by connecting, but let our clients do the selling.',
              'Stay close to the market, but be seen as neutral and independent.',
              'You decide who to introduce. When. How often.',
              'Work with fast-growing tech companies. On your terms.',
            ].map((text, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  elevation={2}
                  sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, display: 'flex', alignItems: 'center', height: '100%' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <CheckCircleIcon
                      color='primary'
                      sx={{ mr: 1, mt: '2px', fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    />
                    <Typography variant='body1' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                      {text}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, height: '100%' }}>
                <Typography variant='h6' fontWeight='bold' mb={1} sx={{ fontSize: { xs: '16px', sm: '18px' } }}>
                  Who is this for?
                </Typography>
                <Typography variant='body2' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                  • You're looking to earn on the side
                </Typography>
                <Typography variant='body2' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                  • You know decision-makers, or can get to them
                </Typography>
                <Typography variant='body2' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                  • A few years of relevant industry experience helps
                </Typography>
                <Typography
                  variant='body2'
                  mt={1}
                  color='primary'
                  fontWeight='bold'
                  sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                >
                  No cold outreach, no pitching — just open doors when it feels right.
                </Typography>
                <Box mt={2} display='flex' alignItems='center'>
                  <Tooltip
                    title="Sign up and get access within a day to view active programs and check if it's a match."
                    arrow
                  >
                    <Button
                      variant='contained'
                      color='primary'
                      component={Link}
                      to='/register'
                      sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                    >
                      Become a Lead Agent
                    </Button>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  border: '2px dashed #1976d2',
                  backgroundColor: '#f0f6ff',
                  height: '100%',
                }}
              >
                <Typography variant='h6' fontWeight='bold' mb={1} sx={{ fontSize: { xs: '16px', sm: '18px' } }}>
                  What's the opportunity?
                </Typography>
                <Typography variant='body2' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                  • Earn $300–$700 per meeting
                </Typography>
                <Typography variant='body2' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                  • Earn % of revenue if a deal closes
                </Typography>
                <Typography variant='body2' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                  • Earn extra bonuses when hitting milestones
                </Typography>
                <Typography
                  variant='body2'
                  mt={1}
                  color='primary'
                  fontWeight='bold'
                  sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                >
                  If a deal closes, one good intro can earn you $4k–$20k+.
                </Typography>
                <Box display='flex' alignItems='center' mt={2}>
                  <Tooltip title='Download PDF with more background' arrow>
                    <Button
                      variant='outlined'
                      color='primary'
                      size='small'
                      sx={{
                        whiteSpace: 'nowrap',
                        minWidth: 'max-content',
                        fontSize: { xs: '14px', sm: '16px' },
                      }}
                      component={Link}
                      to='/content/commissionExamples'
                    >
                      See Commission Example
                    </Button>
                  </Tooltip>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Page 3 - Compact Final Version */}

      <Box id='clients' sx={{ backgroundColor: '#fff' }}>
        <Box
          sx={{
            scrollSnapAlign: 'start',
            minHeight: { xs: 'calc(100dvh - 56px)', sm: 'calc(100dvh - 64px)' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: { xs: 4, sm: 6 },
          }}
        >
          <Box sx={{ maxWidth: 1220 }}>
            <Box mb={{ xs: 3, sm: 4 }} zIndex={2} position='relative'>
              <Typography
                variant='h5'
                fontWeight='bold'
                gutterBottom
                sx={{ fontSize: { xs: '24px', sm: '28px', md: '34px' }, color: 'primary.main' }}
              >
                Get access to decision-makers fast — only pay for results
              </Typography>

              <Typography variant='subtitle1' color='text.secondary' sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                Why emerging tech companies rely on warm intros for fast growth
              </Typography>
            </Box>

            <Box
              container
              display='flex'
              flexDirection={{ xs: 'column', lg: 'row' }}
              gap={{ xs: 2, sm: '26px' }}
              alignItems={{ xs: 'center', sm: 'stretch' }}
            >
              <Box>
                <Paper
                  elevation={3}
                  sx={{ borderRadius: 4, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 4 }, height: '100%' }}
                >
                  <Grid container spacing={2}>
                    {[
                      {
                        text: "Buyers trust industry peers to guide what's worth their time.",
                        icon: <CheckCircleIcon sx={{ color: '#1976d2' }} />,
                      },
                      {
                        text: 'Real meetings happen when intros come from industry insiders.',
                        icon: <CheckCircleIcon sx={{ color: '#1976d2' }} />,
                      },
                      {
                        text: 'Warm intros cut through noise and shorten the cycle.',
                        icon: <CheckCircleIcon sx={{ color: '#1976d2' }} />,
                      },
                      {
                        text: 'Warm intros speed up learning, deals, and growth.',
                        icon: <CheckCircleIcon sx={{ color: '#1976d2' }} />,
                      },
                    ].map((item, idx) => (
                      <Grid item xs={12} key={idx}>
                        <Paper
                          elevation={1}
                          sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, display: 'flex', alignItems: 'center' }}
                        >
                          {item.icon}
                          <Typography variant='body2' ml={1} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                            {item.text}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}

                    <Grid item xs={12}>
                      <Paper
                        elevation={0}
                        sx={{
                          px: { xs: 1.5, sm: 2 },
                          py: { xs: 1.5, sm: 2 },
                          backgroundColor: '#f9f9f9',
                          borderRadius: 2,
                        }}
                      >
                        <Typography
                          variant='caption'
                          display='block'
                          fontStyle='italic'
                          mb={1}
                          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                        >
                          "Buyers are 5x more likely to engage if the outreach is through a mutual connection."{' '}
                          <Box component='span' fontStyle='normal' color='text.secondary'>
                            — LinkedIn
                          </Box>
                        </Typography>
                        <Typography
                          variant='caption'
                          display='block'
                          fontStyle='italic'
                          mb={1}
                          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                        >
                          "B2B buyers spend just 17% of their purchase journey in meetings with sales reps."{' '}
                          <Box component='span' fontStyle='normal' color='text.secondary'>
                            — Gartner
                          </Box>
                        </Typography>
                        <Typography
                          variant='caption'
                          display='block'
                          fontStyle='italic'
                          sx={{ fontSize: { xs: '12px', sm: '14px' } }}
                        >
                          "Companies with referral programs are 2x more likely to have effective sales efforts."{' '}
                          <Box component='span' fontStyle='normal' color='text.secondary'>
                            — Statista
                          </Box>
                        </Typography>
                      </Paper>
                    </Grid>

                    <Grid item xs={12}>
                      <Box mt={2} textAlign='center'>
                        <Tooltip
                          title='Sign up to share your product and commission plan. We may connect you with insiders who know your buyers, and you can also invite your own.'
                          arrow
                        >
                          <Button
                            variant='contained'
                            color='primary'
                            component={Link}
                            to='/register'
                            sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                          >
                            Register as a Client
                          </Button>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              <Box display='flex' justifyContent='center' flexShrink={0}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 4,
                    px: { xs: 2, sm: 4 },
                    py: { xs: 2, sm: 4 },
                    backgroundColor: '#f9fbfd',
                    height: '100%',
                    maxWidth: { xs: '100%', sm: 404 },
                    flexShrink: 0,
                  }}
                >
                  <Typography variant='h6' fontWeight='bold' gutterBottom sx={{ fontSize: { xs: '16px', sm: '18px' } }}>
                    How It Works
                  </Typography>

                  {[
                    { icon: <PersonAddAltIcon />, text: 'You register as a client' },
                    { icon: <DescriptionIcon />, text: 'You enter your commission plan' },
                    { icon: <PhoneInTalkIcon />, text: 'We intro the agent on a 30-min call' },
                    { icon: <ThumbUpIcon />, text: 'You approve the agent' },
                    { icon: <ListAltIcon />, text: 'You approve their target list' },
                    { icon: <CalendarTodayIcon />, text: 'The agent starts booking meetings' },
                  ].map((step, idx, arr) => (
                    <Box key={idx} textAlign='left'>
                      <Box display='flex' alignItems='center' gap={1}>
                        {step.icon}
                        <Typography
                          variant='body2'
                          sx={{ fontSize: { xs: '14px', sm: '16px' } }}
                        >{`${idx + 1}. ${step.text}`}</Typography>
                      </Box>

                      {idx !== arr.length - 1 && (
                        <Box
                          sx={{
                            height: 56,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Box
                            sx={{
                              width: 2,
                              height: 20,
                              borderLeft: '2px dotted #1976d2',
                              position: 'relative',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderTop: '8px solid #1976d2',
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Paper>
              </Box>
            </Box>
          </Box>
        </Box>
        <Footer />
      </Box>
    </Box>
  );
}

export default Home;
